import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OltService } from '@modules/clients/services/olt/olt.service';
import { CommonModule } from '@angular/common';
import * as dayjs from 'dayjs';
import 'dayjs/locale/es';

export interface ILiveStatusResponse {
  data: {
    upload: number;
    download: number;
  };
  extra_data: {
    upload: number;
    download: number;
    uploadpps: string;
    downloadpps: string;
    uploadpeak: number;
    downloadpeak: number;
    interval: number;
    substract_for_current_value: number;
  };
  status: boolean;
  response_code: string;
}

@Component({
  selector: 'app-live-statu-smart-olt-modal',
  standalone: true,
  imports: [HighchartsChartModule, CommonModule],
  templateUrl: './live-statu-smart-olt-modal.component.html',
})
export class LiveStatuSmartOltModalComponent implements OnInit, OnDestroy {
  public isHighcharts = typeof Highcharts === 'object';
  public Highcharts: typeof Highcharts = Highcharts;
  readonly currentTime = dayjs().valueOf(); // Obtiene el timestamp actual en milisegundos
  public upload = [[this.currentTime, 0]];
  public download = [[this.currentTime, 0]];
  public chartOptions = this.getChartOptions();
  public liveStatus: ILiveStatusResponse = {
    data: {
      upload: 0,
      download: 0,
    },
    extra_data: {
      upload: 0,
      download: 0,
      uploadpps: '0',
      downloadpps: '0',
      uploadpeak: 0,
      downloadpeak: 0,
      interval: 0,
      substract_for_current_value: 0,
    },
    status: false,
    response_code: '',
  };

  public intervalId!: any;

  @ViewChild('chart') chartComponent!: {
    chart: { showLoading: (message: string) => void; hideLoading: () => void; series: any[] };
  };

  constructor(
    public readonly dynamicDialogRef: DynamicDialogRef,
    public readonly dynamicDialogConfig: DynamicDialogConfig<{ sn: string; companyOwner: number } | null>,
    private readonly oltService: OltService,
  ) {}

  ngOnInit(): void {
    dayjs.locale('es');
    this.setData();
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
  }

  /**
   * setData
   */
  public async setData() {
    let isFetching: boolean = false;
    let errorFetch: boolean = false;
    this.intervalId = setInterval(async () => {
      if (isFetching || errorFetch) return; // Evita múltiples llamadas simultáneas
      isFetching = true;
      try {
        const response = await this.oltService.getLiveStatus(
          this.dynamicDialogConfig.data?.sn!,
          this.dynamicDialogConfig.data?.companyOwner!,
        );
        this.liveStatus = response;
        // Agrega el timestamp actual junto con los datos de upload y download
        const currentTime = dayjs().valueOf(); // Obtiene el timestamp en milisegundos en la hora local
        this.upload.push([currentTime, response.data.upload]);
        this.download.push([currentTime, response.data.download]);

        // Actualiza el gráfico dinámicamente
        if (this.chartComponent && this.chartComponent.chart) {
          this.chartComponent.chart.series[0].setData(this.upload, true); // Actualiza la serie de Upload
          this.chartComponent.chart.series[1].setData(this.download, true); // Actualiza la serie de Download
        }
      } catch (error) {
        console.error('Error al obtener el estado en vivo:', error);
        errorFetch = true; // Permite nuevas llamadas
      } finally {
        isFetching = false; // Permite nuevas llamadas
      }
    }, 3000);
  }

  getChartOptions() {
    return {
      accessibility: {
        enabled: false,
      },
      time: {
        useUTC: false, // Desactiva el uso de UTC
      },

      title: {
        text: '',
        align: 'center',
      },
      xAxis: {
        type: 'datetime', // Configura el eje X como un eje de tiempo
        tickInterval: 8 * 1000, // Intervalo de 1 minuto entre ticks
      },
      yAxis: {
        title: {
          text: 'Mbps',
        },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
      },
      tooltip: {
        headerFormat: '<span style="color:{point.color}">\u25CF</span> <b>{series.name}</b><br>',
        pointFormatter: function () {
          const date = new Date(this.x); // Convierte el timestamp a una fecha
          const options: Intl.DateTimeFormatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true, // Activa el formato de 12 horas
          };
          const formattedDate = date.toLocaleDateString('es-ES', options); // Formatea la fecha en español
          return `Hora: ${formattedDate}<br>Valor: ${this.y} Mbps`;
        },
      },
      credits: { enabled: false },
      series: [
        {
          type: 'areaspline',
          name: 'Upload',
          data: this.upload,
          color: 'rgba(255, 86, 86, 1)',
          marker: {
            radius: 4,
            symbol: 'circle',
            enabled: true,
          },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(255, 86, 86, 0.5)'],
              [0.4, 'rgba(255, 86, 86, 0.3)'],
              [1, 'rgba(255, 233, 233, 0.3)'],
            ],
          },
        },
        {
          type: 'areaspline',
          name: 'Download',
          data: this.download,
          color: 'rgba(66, 133, 244)',
          marker: {
            radius: 4,
            symbol: 'circle',
            enabled: true,
          },
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(66, 133, 244, 0.5)'],
              [0.4, 'rgba(66, 133, 244, 0.3)'],
              [1, 'rgba(233, 233, 255, 0.3)'],
            ],
          },
        },
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
              },
            },
          },
        ],
      },
    } as Highcharts.Options;
  }
}
